import React, { useContext, useEffect, useState, memo, useRef } from 'react';
import { TextField, Box, IconButton, CircularProgress, useTheme } from '@mui/material';

import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import Tooltip from '@mui/material/Tooltip';
import './OrderForm.css';
import { MagicWand01 } from '../../../assets';
import { OrderFormItem, OrderFormItemUpdate } from '../../../app/services/api/orderManagementClient';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import AiOrderExplanationButton from '../../../components/shared/orders/AiOrderExplanationButton';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const parseDraftQuantity = (value: string | null | undefined): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const normalizeAiLookupDate = (value: string): string | null => {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const dayFirst = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (dayFirst) return `${dayFirst[3]}-${dayFirst[2]}-${dayFirst[1]}`;

  const monthFirst = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (monthFirst) return `${monthFirst[3]}-${monthFirst[1]}-${monthFirst[2]}`;

  return null;
};

const encodeBase64Url = (value: string): string => {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

interface QuantityCellProps {
  item: OrderFormItem;
  dateIndex: number;
  currentValue: string;
  handleSingleQuantityChange: (
    newQuantity: number | null,
    sku: string,
    itemId: number,
    dateIndex: number
  ) => Promise<void>;
  canSave: boolean;
  isError: boolean;
  helperText: string | null;
  showPrediction?: boolean;
  predictedQuantity?: number;
  aiRecommendedQuantity?: number | null;
  onMagicWandClick?: () => void;
  borderColor?: string;
  maxOrderQuantity?: number;
  predictedDemandQuantity?: number;
  minStockOnHandQty?: number;
  primaryUom?: string;
  date: string;
  upsertOrderFormItemUpdate: (single: OrderFormItemUpdate) => void;
  canEditPredictions?: boolean;
  isParEnabled?: boolean;
  parQuantity?: number | null;
  isQuantityOverridden?: boolean;
}

const QuantityCell: React.FC<QuantityCellProps> = memo(function QuantityCell({
  item,
  dateIndex,
  currentValue,
  handleSingleQuantityChange,
  canSave,
  isError,
  helperText,
  showPrediction,
  predictedQuantity,
  aiRecommendedQuantity,
  onMagicWandClick,
  borderColor,
  maxOrderQuantity,
  predictedDemandQuantity,
  minStockOnHandQty,
  date,
  upsertOrderFormItemUpdate,
  canEditPredictions = true,
  isParEnabled = false,
  parQuantity,
  isQuantityOverridden = false,
}) {
  const [localValue, setLocalValue] = useState(currentValue);
  const latestLocalValue = useRef(localValue);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const originalFocusValue = useRef<string | null>(null);
  const isFocused = useRef(false);
  const { t } = useTranslation('orderForm');
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const theme = useTheme();

  // this is called when it goes out of scope, will use ref of first focus.
  // will mean it will save when there is a change and then the row gets de rendered by the vitualiser
  useEffect(() => {
    return () => {
      const originalQuantity = parseDraftQuantity(originalFocusValue.current ?? currentValue);
      const latestVal = latestLocalValue.current;
      const newQuantity = parseDraftQuantity(latestVal);

      if (newQuantity === originalQuantity) return;
      if (warningMessage) return;

      handleSingleQuantityChange(
        newQuantity,
        item.sku,
        item.id,
        dateIndex,
      ).catch(() => { });
    };
  }, []);

  useEffect(() => {
    latestLocalValue.current = localValue;
  }, [localValue]);

  useEffect(() => {
    if (!isFocused.current && currentValue !== localValue) {
      setLocalValue(currentValue);
      latestLocalValue.current = currentValue;
    }
  }, [currentValue, localValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    // When PAR is enabled, allow empty string to mean "let PAR fill this"
    if (isParEnabled && rawValue === '') {
      setLocalValue('');
      const singleItemUpdate = {
        id: item.id,
        sku: item.sku,
        dates: [{ date, quantity: null }],
      };
      upsertOrderFormItemUpdate(singleItemUpdate);
      setWarningMessage(null);
      return;
    }

    const value = rawValue || '0';
    let numeric = Number(value);
    if (numeric < 0) numeric = 0;
    if (value.includes('.')) numeric = Math.round(numeric);

    setLocalValue(numeric.toString());

    const singleItemUpdate = {
      id: item.id,
      sku: item.sku,
      dates: [{ date, quantity: numeric }],
    };

    upsertOrderFormItemUpdate(singleItemUpdate);
    if (!isParEnabled && item.minQuantity != null && numeric < item.minQuantity && numeric > 0) {
      setWarningMessage(`${t('minQuantityAllowedIs')} ${item.minQuantity}`);
    } else if (!isParEnabled && item.maxQuantity != null && numeric > item.maxQuantity && numeric > 0) {
      setWarningMessage(`${t('maxQuantityAllowedIs')} ${item.maxQuantity}`);
    } else {
      setWarningMessage(null);
    }
  };

  const onBlur = async () => {
    isFocused.current = false;

    const originalQuantity = parseDraftQuantity(originalFocusValue.current);
    const newQuantity = parseDraftQuantity(localValue);

    if (newQuantity === originalQuantity) return;

    if (warningMessage && !(isParEnabled && Number(localValue) === 0)) return;
    setSaveStatus('saving');
    try {
      await handleSingleQuantityChange(newQuantity, item.sku, item.id, dateIndex);
      setSaveStatus('saved');

      if (isParEnabled) {
        originalFocusValue.current = currentValue;
        setLocalValue(currentValue);
        latestLocalValue.current = currentValue;
      } else {
        originalFocusValue.current = localValue;
      }

      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
    }
  };

  const handleMagicWandClick = () => {
    if (predictedQuantity != null && Number(localValue) !== predictedQuantity) {
      setLocalValue(predictedQuantity.toString());

      if (item.minQuantity != null && predictedQuantity < item.minQuantity) {
        setWarningMessage(`${t('minQuantityAllowedIs')} ${item.minQuantity}`);
      } else if (item.maxQuantity != null && predictedQuantity > item.maxQuantity) {
        setWarningMessage(`${t('maxQuantityAllowedIs')} ${item.maxQuantity}`);
      } else {
        setWarningMessage(null);
        onMagicWandClick?.();
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const persistedOrderId = item.orderId?.[dateIndex];
  const persistedOrderItemId = item.orderItemId?.[dateIndex];
  const lookupDate = normalizeAiLookupDate(date);
  const syntheticOrderId = lookupDate ? `date-${lookupDate}` : null;
  const syntheticOrderItemId = item.sku ? `sku-${encodeBase64Url(item.sku)}` : null;
  const orderId: number | string | null = persistedOrderId ?? syntheticOrderId;
  const orderItemId: number | string | null = persistedOrderItemId ?? syntheticOrderItemId;
  const canViewAiExplanation = hasPermissionTo(['Admin']);
  const canResolveAiExplanation = orderId != null && orderItemId != null;

  return (
    <Box
      className="quantity-input-container"
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: 50,
        maxWidth: 140,
        mb: 0,
      }}
    >
      <TextField
        type="number"
        label={
          showPrediction && canEditPredictions
            ? t('predictedQuantity', { value: predictedQuantity?.toString() ?? '' })
            : isParEnabled && parQuantity != null
              ? t('parLabel')
              : null
        }
        value={localValue}
        placeholder={isParEnabled && localValue === '' ? (parQuantity != null ? parQuantity.toString() : '') : undefined}
        onChange={handleInputChange}
        onBlur={onBlur}
        disabled={!canSave || (showPrediction && !canEditPredictions)}
        error={isError}
        style={{ width: '100%', minWidth: 50, height: 40, marginBottom: 0, textAlign: 'center' }}
        helperText={helperText}
        autoComplete="off"
        onFocus={(e) => {
          isFocused.current = true;
          e.target.addEventListener('wheel', (evt) => evt.preventDefault(), { passive: false });
          if (originalFocusValue.current === null) {
            originalFocusValue.current = localValue;
          }
          e.target.select();
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: borderColor,
            },
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
              display: (!canSave || (showPrediction && !canEditPredictions)) ? 'none' : 'block',
              opacity: 1,
            },
          },
          '& .MuiInputLabel-root': {
            color: borderColor,
          },
          width: '100%',
          '& input': {
            textAlign: 'center',
            padding: '8.5px 14px',
          },
        }}
        size="small"
      />

      {showPrediction && canEditPredictions && onMagicWandClick && (
        <Box className="quantity-magic-wand">
          <Tooltip
            title={
              <span>
                {predictedDemandQuantity != null && (
                  <>
                    {t('predictedDemandQuantity', {
                      value: predictedDemandQuantity,
                      uom: item.primaryUom,
                    })}
                    <br />
                  </>
                )}
                {maxOrderQuantity != null && (
                  <>
                    {t('maxOrderQuantity', { value: maxOrderQuantity, uom: item.primaryUom })}
                    <br />
                  </>
                )}
                {minStockOnHandQty != null &&
                  t('safetyStock', { value: minStockOnHandQty, uom: item.primaryUom })}
              </span>
            }
          >
            <IconButton aria-label={t('applyPredictedQuantity')}
              onClick={handleMagicWandClick}>
              <MagicWand01
                color={borderColor ? theme.palette.success[700] : theme.palette.custom.gray[400]}
              />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {canViewAiExplanation && canResolveAiExplanation && orderId != null && orderItemId != null && (
        <Box className="quantity-ai-explanation">
          <AiOrderExplanationButton
            translationNamespace="orderForm"
            triggerVariant="help"
            recommendedQuantity={aiRecommendedQuantity}
            finalQuantity={Number(localValue)}
            isQuantityOverridden={isQuantityOverridden}
            primaryUom={item.primaryUom}
            orderableUom={item.orderableUom}
            storeNumber={selectedStore?.storeNumber}
            orderId={orderId as unknown as number}
            orderItemId={orderItemId as unknown as number}
          />
        </Box>
      )}

      {saveStatus === 'saving' && (
        <Box sx={{ position: 'absolute', right: -5, top: -5 }}>
          <CircularProgress size={16} />
        </Box>
      )}
      {saveStatus === 'saved' && (
        <Box sx={{ position: 'absolute', right: -5, top: -5, color: theme.palette.success.main }}>
          <CloudDoneIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
      {saveStatus === 'error' && (
        <Box sx={{ position: 'absolute', right: -5, top: -5, color: theme.palette.error.main }}>
          <ErrorIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
      {saveStatus === 'idle' && warningMessage && (
        <Tooltip title={warningMessage}>
          <Box sx={{ position: 'absolute', right: -5, top: -5, color: theme.palette.warning.main }}>
            <WarningIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
});

export default QuantityCell;
